"use client";
import React from "react";
import Image from "next/image";
import { usePathname } from "next/navigation";

export default function FinancialPlan() {
  const pathname = usePathname();
  const financialSteps = [
    {
      id: 1,
      title: "Choose Your Financial Goal",
      imgSrc: "/next_assets/images/financial-planning/financial-1.svg",
      imgSrcMob: "/next_assets/images/financial-planning/financialmob-1.svg",
    },
    {
      id: 2,
      title: "Share a Few Key Details",
      imgSrc: "/next_assets/images/financial-planning/financial-2.svg",
      imgSrcMob: "/next_assets/images/financial-planning/financialmob-2.svg",
    },
    {
      id: 3,
      title: "Let Our Research-Driven Engine Do the Math",
      imgSrc: "/next_assets/images/financial-planning/financial-3.svg",
      imgSrcMob: "/next_assets/images/financial-planning/financialmob-3.svg",
    },
    {
      id: 4,
      title: "Get a Customized Investment Plan",
      imgSrc: "/next_assets/images/financial-planning/financial-4.svg",
      imgSrcMob: "/next_assets/images/financial-planning/financialmob-4.svg",
    },
    {
      id: 5,
      title: "Adjust, Compare & Personalize Further",
      imgSrc: "/next_assets/images/financial-planning/financial-5.svg",
      imgSrcMob: "/next_assets/images/financial-planning/financialmob-5.svg",
    },
    {
      id: 6,
      title: "Get Support, Stay on Track",
      imgSrc: "/next_assets/images/financial-planning/financial-6.svg",
      bgimg: "/next_assets/images/financial-planning/financial-bg-6.svg",
      imgSrcMob: "/next_assets/images/financial-planning/financialmob-6.svg",
    },
  ];

  const isChild = pathname.includes("/financial-planning/child-future-planning");
  const isRetirement = pathname.includes("/financial-planning/retirement-planning");

  return (
    <section className="mb-17.5 xxs:mb-100 2md:mb-20 2xl:mb-100 text-center">
      <h2 className="text-[24px] leading-8 text-center 2md:text-2.5xl 2md:leading-9 2xl:text-[32px] 2xl:leading-10 font-semibold text-primaryBlack mb-2.5">
        {isChild
          ? "How to Plan for Your Child’s Future in 6 Simple Steps"
          : isRetirement
            ? "See How Our Retirement Planning Works for Your Goals"
            : "Explore How Our Financial Planning Works For Your Future"}
      </h2>
      <p className="2md:text-lg 2xl:text-xl font-normal text-primaryBlack mb-5">
        {isChild
          ? "Set A Goal, Share A Few Details, And Get a Plan Built Specifically for Your Child."
          : isRetirement
            ? "Start your journey to retirement savings with ease, craft your plan, compare options and get ongoing support for confident investments in minutes."
            : " Take out only 2 minutes to kick start your goal-based investment planning and secure your financial future now."}
      </p>

      <div className="flex flex-wrap gap-x-10 gap-y-6 xxs:gap-12 justify-center mt-6 2md:mt-12">
        {financialSteps.map((step) => (
          <div key={step.id} className="relative w-36 xxs:w-56 2md:max-w-72 2xl:max-w-80 2md:w-full">
            <p className="absolute top-2 2md:-top-5 2xl:-top-7.5 left-2 2md:-left-5 2xl:-left-7.5 text-xs 2md:text-xl 2xl:text-4xl font-bold text-[#ffffff] rounded-full p-1 2md:p-2 2xl:p-4 bg-[radial-gradient(90.28%_105.01%_at_112.72%_118.51%,#ABAFB4_0%,#1A64AA_100%)] border-2 border-primaryBlue">
              {step.id.toString().padStart(2, "0")}
            </p>

            <div
              className="border-2 border-primaryBlue rounded-2xl 2md:rounded-7xl p-4 2md:p-5 2xl:p-7.5 mb-3 2md:mb-5 bg-cover bg-center"
              style={{
                backgroundImage: step.bgimg
                  ? `url(${step.bgimg}), radial-gradient(90.28% 105.01% at 112.72% 118.51%, #FFFFFF 0%, #1A4CAA 100%)`
                  : `radial-gradient(90.28% 105.01% at 112.72% 118.51%, #FFFFFF 0%, #1A4CAA 100%)`,
                backgroundRepeat: "no-repeat",
                backgroundSize: step.bgimg ? "contain, cover" : "cover",
              }}
            >
              <Image src={step.imgSrc} alt={step.title} width={243} height={110} className="m-auto hidden 2md:flex" />
              <Image
                src={step.imgSrcMob}
                alt={step.title}
                width={140}
                height={140}
                className="m-auto flex 2md:hidden"
              />
            </div>

            <div className="text-primaryBlack 2md:text-lg 2xl:text-2xl font-semibold">{step.title}</div>
          </div>
        ))}
      </div>
    </section>
  );
}
